// While a video ad runs in the main player, a sheet below it names the company behind the ad.
//
// The company is looked up as soon as the player has loaded the ad (`app:player:adLoaded`). A break
// whose company is not known by `app:player:adStarted` gets no sheet rather than one that fills in mid-ad.

import { pauseAndClickAd, type AdClickTracking } from 'player/javascripts/player/advertising'
import {
  fetchAdCompany,
  type AdCompany,
  type AdContext,
} from 'player/javascripts/player/ad_company'
import abTests from 'frontend/shared/javascripts/modules/ab_tests'
import { trackElementBox } from 'shared/javascripts/util/element_box'
import { on } from 'shared/javascripts/util/events'
import { trackInteraction } from '../tracking'
import type { PlayerElement } from '../util/load_player'

const CTA_CLICK_TRACKING: AdClickTracking = {
  adClickElement: 'main_company_ad_slider',
  elementText: 'Mehr erfahren',
}

const SWIPE_CLOSE_DISTANCE = 60

/** What the page knows and the player does not. */
interface CompanyAdSliderData {
  targetGroupVariant: string
  videoCategory: string
  /** With a `:company_identifier` placeholder, see `fetchAdCompany`. */
  advertisementUrlTemplate: string
}

interface RunningAd {
  context: AdContext
  company: AdCompany
  player: PlayerElement
}

up.compiler(
  '[data-company-ad-slider]',
  (
    element: HTMLElement,
    { targetGroupVariant, videoCategory, advertisementUrlTemplate }: CompanyAdSliderData,
  ) => {
    const sheet = element.querySelector<HTMLElement>('[data-company-ad-slider--sheet]')!
    // Measured instead of the slot: on mobile the player goes sticky while the slot scrolls away.
    const player =
      element.parentElement?.querySelector<HTMLElement>(':scope > .player-toggle-content') ??
      element
    const logo = element.querySelector<HTMLImageElement>('[data-company-ad-slider--logo]')!
    const cta = element.querySelector<HTMLAnchorElement>('[data-company-ad-slider--cta]')!
    const closeButton = element.querySelector<HTMLElement>('[data-company-ad-slider--close]')!
    const header = element.querySelector<HTMLElement>('[data-company-ad-slider--header]')!
    const advertiserName = element.querySelector<HTMLElement>(
      '[data-company-ad-slider--advertiser-name]',
    )!

    let variantActive = false
    let runningAd: RunningAd | null = null
    // Keyed by identifier, so a slow response from an earlier break cannot label a later one. null while
    // the lookup is in flight.
    const companies = new Map<string, AdCompany | null>()
    let loadedCompanyIdentifier: string | null = null
    let dismissedInThisAdBreak = false
    let stopTrackingColumn: (() => void) | null = null

    const isOpen = (): boolean => sheet.classList.contains('-open')

    function trackingParams(): Record<string, unknown> {
      return {
        advertiser_name: runningAd?.company.name,
        creative_id: runningAd?.context.creativeId,
        line_item_id: runningAd?.context.lineItemId,
        target_group_variant: targetGroupVariant,
        video_category: videoCategory,
      }
    }

    function open(ad: RunningAd): void {
      runningAd = ad

      advertiserName.textContent = ad.company.name
      cta.href = ad.context.clickThroughUrl
      showLogo(ad.company.logoUrl)

      sheet.classList.add('-open')
      stopTrackingColumn ??= trackElementBox(player, sheet, '--company-ad-slider', {
        trackScroll: true,
      })

      // tracking: company_ad_slider.shown
      trackInteraction(
        'element.visible',
        { id: 'ad_sheet_shown', params: trackingParams(), text: 'ad_sheet' },
        sheet,
      )
    }

    function close(): void {
      sheet.classList.remove('-open', '-dragging')
      sheet.style.removeProperty('--drag-offset')
      stopTrackingColumn?.()
      stopTrackingColumn = null
    }

    // In the compiler rather than an inline onerror attribute, which the CSP forbids.
    logo.addEventListener('error', () => {
      logo.hidden = true
    })

    function showLogo(logoUrl: string | null): void {
      logo.hidden = !logoUrl
      if (logoUrl) {
        logo.src = logoUrl
      }
    }

    function dismiss(): void {
      if (!isOpen()) {
        return
      }
      dismissedInThisAdBreak = true
      // tracking: company_ad_slider.dismissed
      trackInteraction(
        'click.element',
        { id: 'ad_sheet_dismissed', params: trackingParams() },
        closeButton,
      )
      close()
    }

    function onAdLoaded(event: Event & { adContext?: AdContext | null }): void {
      if (!fromMainPlayer(event)) {
        return
      }
      // Kept for a variant decided after the ad loaded: a preroll loads as soon as the page does.
      loadedCompanyIdentifier = event.adContext?.companyIdentifier ?? null
      lookUpLoadedCompany()
    }

    // Deliberately not awaited: neither the ad call nor the ad start may wait for this.
    function lookUpLoadedCompany(): void {
      const companyIdentifier = loadedCompanyIdentifier
      if (!variantActive || !companyIdentifier || companies.has(companyIdentifier)) {
        return
      }
      companies.set(companyIdentifier, null)
      void fetchAdCompany(companyIdentifier, advertisementUrlTemplate).then((company) => {
        // A failed lookup is retried with the next break.
        if (company) {
          companies.set(companyIdentifier, company)
        } else {
          companies.delete(companyIdentifier)
        }
      })
    }

    function onAdStarted(event: Event & { adContext?: AdContext | null }): void {
      const context = event.adContext
      if (!variantActive || dismissedInThisAdBreak || !context || !fromMainPlayer(event)) {
        return
      }
      const company = companies.get(context.companyIdentifier)
      if (company) {
        open({ context, company, player: event.target as PlayerElement })
      }
    }

    function onAdFinished(event: Event): void {
      if (!fromMainPlayer(event)) {
        return
      }
      runningAd = null
      close()
    }

    function onAdBreakEnded(event: Event): void {
      if (!fromMainPlayer(event)) {
        return
      }
      dismissedInThisAdBreak = false
      onAdFinished(event)
    }

    function onCtaClick(event: MouseEvent): void {
      event.preventDefault()

      const videojsPlayer = runningAd?.player.player?.videojsPlayer
      if (!videojsPlayer) {
        return
      }

      // tracking: company_ad_slider.cta_clicked
      trackInteraction(
        'click.element',
        { id: 'ad_sheet_cta_clicked', params: trackingParams() },
        cta,
      )

      // Through the player, so the ad server counts the click on the running creative.
      pauseAndClickAd(videojsPlayer, CTA_CLICK_TRACKING)
    }

    const destructors = [
      on('app:player:adLoaded', onAdLoaded),
      on('app:player:adStarted', onAdStarted),
      on('app:player:adFinished', onAdFinished),
      on('app:player:adBreakFinished app:player:noAdReceived', onAdBreakEnded),
      on(closeButton, 'click', dismiss),
      on(cta, 'click', onCtaClick),
      onSwipeDown(header, sheet, dismiss),
      close, // releases the column tracking if the fragment goes away while the sheet is open
      abTests.onVariant('video_company_ad_slider', (variant) => {
        variantActive = variant === 'b'
        if (variantActive) {
          lookUpLoadedCompany()
        } else {
          close()
        }
      }),
    ]

    return () => destructors.forEach((destructor) => destructor())
  },
)

// Video ad banners run players of their own and emit the same events.
function fromMainPlayer(event: Event): boolean {
  const target = event.target as Element | null
  return !!target && !target.closest('[data-video-ad]')
}

function onSwipeDown(handle: HTMLElement, sheet: HTMLElement, onClose: () => void): () => void {
  const isOpen = (): boolean => sheet.classList.contains('-open')
  let startY: number | null = null

  const end = (event: PointerEvent): void => {
    if (startY === null) {
      return
    }
    const dragged = event.clientY - startY
    cancel()
    if (dragged > SWIPE_CLOSE_DISTANCE) {
      onClose()
    }
  }

  const cancel = (): void => {
    startY = null
    sheet.classList.remove('-dragging')
    sheet.style.removeProperty('--drag-offset')
  }

  const destructors = [
    on(handle, 'pointerdown', (event: PointerEvent) => {
      // A mouse drag on the sheet is a text selection, not a swipe.
      if (event.pointerType !== 'mouse' && isOpen()) {
        startY = event.clientY
        sheet.classList.add('-dragging')
        // An upward drag is clamped to 0, so the finger leaves the header and would take pointerup
        // with it. Throws for a pointer that is no longer active, which is not worth reacting to.
        try {
          handle.setPointerCapture(event.pointerId)
        } catch {
          // no capture, the drag still works as long as the finger stays on the header
        }
      }
    }),
    on(handle, 'pointermove', (event: PointerEvent) => {
      if (startY === null) {
        return
      }
      if (isOpen()) {
        sheet.style.setProperty('--drag-offset', `${Math.max(0, event.clientY - startY)}px`)
      } else {
        cancel()
      }
    }),
    on(handle, 'pointerup pointercancel', end),
  ]

  return () => destructors.forEach((destructor) => destructor())
}
